import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode
} from 'react';

interface MobileHeaderPanelContextValue {
    panelContent: ReactNode | null;
    panelTitle: string | null;
    panelTooltip: string | null;
    setPanelContent: (content: ReactNode) => void;
    setPanelMeta: (title: string | null, tooltip: string | null) => void;
    clearPanelContent: () => void;
}

const MobileHeaderPanelContext = createContext<MobileHeaderPanelContextValue>({
    panelContent: null,
    panelTitle: null,
    panelTooltip: null,
    setPanelContent: () => {},
    setPanelMeta: () => {},
    clearPanelContent: () => {}
});

export const MobileHeaderPanelProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [content, setContent] = useState<ReactNode | null>(null);
    const [panelTitle, setPanelTitle] = useState<string | null>(null);
    const [panelTooltip, setPanelTooltip] = useState<string | null>(null);

    const setPanelContent = useCallback((newContent: ReactNode) => {
        setContent(newContent);
    }, []);

    const setPanelMeta = useCallback((title: string | null, tooltip: string | null) => {
        setPanelTitle(title);
        setPanelTooltip(tooltip);
    }, []);

    const clearPanelContent = useCallback(() => {
        setContent(null);
        setPanelTitle(null);
        setPanelTooltip(null);
    }, []);

    const value = useMemo(
        () => ({ panelContent: content, panelTitle, panelTooltip, setPanelContent, setPanelMeta, clearPanelContent }),
        [content, panelTitle, panelTooltip, setPanelContent, setPanelMeta, clearPanelContent]
    );

    return <MobileHeaderPanelContext.Provider value={value}>{children}</MobileHeaderPanelContext.Provider>;
};

export const useMobileHeaderPanel = () => useContext(MobileHeaderPanelContext);

export const useMobileHeaderPanelContent = (content: ReactNode, options?: { title?: string; tooltip?: string }) => {
    const { setPanelContent, setPanelMeta, clearPanelContent } = useMobileHeaderPanel();

    const clearRef = useRef(clearPanelContent);
    clearRef.current = clearPanelContent;

    const contentRef = useRef(content);
    contentRef.current = content;

    useLayoutEffect(() => {
        setPanelContent(contentRef.current);
    }, [content, setPanelContent]);

    useEffect(() => {
        if (options?.title != null || options?.tooltip != null) {
            setPanelMeta(options?.title ?? null, options?.tooltip ?? null);
        }
    }, [options?.title, options?.tooltip, setPanelMeta]);

    useEffect(() => {
        return () => clearRef.current();
    }, []);
};
